import { Accordion, Card } from '@wundo/ui';
import type { RoleplayCard } from '@wundo/shared/roleplay.ts';
import { PracticeCard, type PracticeCardProps } from './PracticeCard.tsx';
import { PracticeLibraryOrder } from './PracticeLibraryOrder.tsx';
import { SortablePracticeCard } from './SortablePracticeCard.tsx';
import { m } from '@/paraglide/messages.js';

/** The two lists on Oefenen: the organisation's library and the user's own. */
export type PracticeTab = 'library' | 'personal';

export interface PracticeCardListProps {
  tab: PracticeTab;
  cards: RoleplayCard[];
  loading: boolean;
  failed: boolean;
  startFailed: boolean;
  /** Arranging the library was refused and the list snapped back. */
  moveFailed: boolean;
  /** The library, for an admin who arranges it. */
  sortable: boolean;
  openIds: string[];
  onOpenChange: (ids: string[]) => void;
  /** What every card in the list gets, arranged or not. */
  cardProps: (card: RoleplayCard) => Omit<PracticeCardProps, 'handle'>;
  onReorder: (ids: string[]) => void;
}

/** One tab of Oefenen: its cards, one open at a time, or why there are none. */
export function PracticeCardList({
  tab,
  cards,
  loading,
  failed,
  startFailed,
  moveFailed,
  sortable,
  openIds,
  onOpenChange,
  cardProps,
  onReorder,
}: PracticeCardListProps) {
  const empty = !loading && !failed && cards.length === 0;

  return (
    <div className='mt-4.5'>
      {loading && <p className='text-base text-ink-muted'>{m.practice_loading()}</p>}
      {failed && <p className='text-base text-danger'>{m.practice_failed()}</p>}
      {startFailed && (
        <p role='alert' className='mb-3 text-base text-danger'>
          {m.practice_start_failed()}
        </p>
      )}
      {moveFailed && (
        <p role='alert' className='mb-3 text-base text-danger'>
          {m.practice_move_failed()}
        </p>
      )}

      {empty && tab === 'library' && (
        <PracticeEmpty
          title={m.practice_empty_library_title()}
          body={m.practice_empty_library_body()}
        />
      )}

      {/*
        No button to make one: a personal practice conversation comes out of
        a chat, and pointing at a page that cannot create one would promise
        something that is not there yet.
      */}
      {empty && tab === 'personal' && (
        <PracticeEmpty
          title={m.practice_empty_personal_title()}
          body={m.practice_empty_personal_body()}
        />
      )}

      {cards.length > 0 && (
        <Accordion
          className='gap-3.5'
          value={openIds}
          onValueChange={(value) => onOpenChange(value as string[])}
        >
          {/*
            Only the library has an order somebody decided. The personal list
            is one person's own and is read newest first.
          */}
          {sortable ? (
            <PracticeLibraryOrder cards={cards} onReorder={onReorder}>
              {(card, onMove) => (
                <SortablePracticeCard
                  key={card.id}
                  {...cardProps(card)}
                  onMove={onMove}
                />
              )}
            </PracticeLibraryOrder>
          ) : (
            cards.map((card) => <PracticeCard key={card.id} {...cardProps(card)} />)
          )}
        </Accordion>
      )}
    </div>
  );
}

/**
 * A list with nothing in it, in the same bordered card as a roleplay, read left
 * to right. Not the centred `EmptyState`: a short line centred over a card row
 * reads as a card that failed to render rather than as an answer.
 */
function PracticeEmpty({ title, body }: { title: string; body: string }) {
  return (
    <Card as='section' className='max-w-165'>
      <h2 className='text-md font-bold text-ink'>{title}</h2>
      <p className='mt-1.5 text-base leading-relaxed text-ink-muted'>{body}</p>
    </Card>
  );
}
